import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { getInspectionStandardTypeOptions } from '../../customerSettings';
import { ITranslateProps, translate } from '../../mixins/translate';
import { fetchDelete, fetchGet, fetchPost } from '../../store/fetch';
import { codeToColorCode } from '../../utils/conditionHelper';
import { BatchStates } from '../../utils/enums/BatchStates';
import { Roles } from '../../utils/enums/Roles';
import { IRouterProps } from '../../utils/IRouterProps';
import {
    EditPanelButton,
    EditPanelButtons,
    EditPanelSection,
    EditPanelSwitch,
    InputSearchText,
    PanelBodyHeading,
    PanelWithoutForm,
    PopupElement,
    SmallFilterSelect,
    SmallSelect,
} from '../elements';
import { Spinner } from '../spinners';
import { Component } from '../utils/Component';
import { Bounds, GenericMap, IMarker } from '../utils/GenericMap';

interface IBatchesMapProps extends ITranslateProps, IRouterProps {
    languageCode: string;
    userRole: Roles;
    isPdokMapLayerAvailable: boolean;
    isDroneNoFlyMapNlAvailable: boolean;
}

interface IBatch {
    id: string;
    name: string;
    status: string;
    batchObject: { id: string };
}

interface IObject {
    id: string;
    name: string;
    location: string;
    latitude?: undefined | number;
    longitude?: undefined | number;
    lastSurveyCondition?: undefined | string;
    lastSurveyInspectionStandardType?: undefined | string;
    lastSurveyId?: undefined | string;
    surveryedOn?: undefined | Date;
    shapeGeoJson?: undefined | string;
}

interface IValueText {
    value: string;
    text: string;
}

interface IBatchesMapState {
    loading: boolean;
    searching: boolean;
    items: IObject[];
    latitude: number;
    longitude: number;
    zoom: number;
    currentIndex: number;
    currentTitle: string;
    currentSurveyDate: Date;
    currentId: string;
    objectTypes: IValueText[];
    batches: { id: string; name: string; status: string }[];
    objectTypeId?: undefined | string;
    batchId?: undefined | string;
    batchSearchText?: undefined | string;
    inspectionStandardType?: undefined | string;
    batchStatus?: undefined | string;
    riskAssessment?: undefined | string;
    showObjectsNotInSelection: boolean;
    selectedObject?: undefined | { id: string; name: string };
    selectedObjectBatches: { id: string; batch: IBatch }[];
    canSelectCompletedBatches: boolean;
    fixedBatchId?: undefined | string;
    fixedBatchName?: undefined | string;
    mapBounds?: undefined | Bounds;
    markersToFit?: undefined | IMarker[];
    isFirstSearch: boolean;
}

function mapStateToProps(state: any) {
    const { authentication, language } = state;
    return {
        languageCode: language.code,
        userRole: authentication.role,
        isPdokMapLayerAvailable: authentication.isPdokMapLayerAvailable,
        isDroneNoFlyMapNlAvailable: authentication.isDroneNoFlyMapNlAvailable,
    };
}

class BatchesMapComponent extends Component<IBatchesMapProps, IBatchesMapState> {
    constructor(props: IBatchesMapProps) {
        super(props);

        this.state = {
            loading: true,
            searching: false,
            items: [],
            latitude: 51.808504,
            longitude: 5.703935,
            zoom: 2,
            currentIndex: 0,
            currentTitle: '',
            currentSurveyDate: new Date(),
            currentId: '',
            objectTypes: [],
            batches: [],
            showObjectsNotInSelection: false,
            canSelectCompletedBatches: false,
            fixedBatchId: props.location.query.batchId,
            selectedObjectBatches: [],
            isFirstSearch: true,
        };
    }

    public override async componentDidMount() {
        if (this.state.fixedBatchId) {
            await this.loadFixedBatchName();
        }
        await this.loadObjectTypes();
        await this.loadBatches();
        await this.search();
    }

    public override async componentDidUpdate(prevProps: IBatchesMapProps, prevState: IBatchesMapState) {
        if (this.props.languageCode !== prevProps.languageCode) {
            await this.loadObjectTypes();
            await this.search();
        }
        if (
            this.state.batchSearchText !== prevState.batchSearchText ||
            this.state.batchStatus !== prevState.batchStatus ||
            this.state.inspectionStandardType !== prevState.inspectionStandardType
        ) {
            await this.search();
        } else if (
            this.state.objectTypeId !== prevState.objectTypeId ||
            this.state.showObjectsNotInSelection !== prevState.showObjectsNotInSelection
        ) {
            await this.search();
        }
    }

    public override render() {
        return (
            <span>
                <header id="topbar" className="ph10">
                    <div className="topbar-left">
                        <ol className="breadcrumb">
                            <li className="crumb-active">
                                <span className="fa fa-camera pr10"></span>
                                <Link to="/Batches">{this.props.t('batch.batches')}</Link>
                            </li>
                        </ol>
                    </div>
                </header>

                <section id="content" className="pn posr">
                    <div style={{ position: 'relative', height: '100%' }}>
                        <form method="post" action="/" id="filter">
                            <div className="map-menu admin-form">
                                {this.state.fixedBatchId && (
                                    <div className="mt10">
                                        <button
                                            type="button"
                                            className="btn btn-default btn-xs"
                                            style={{ marginBottom: '1px' }}
                                            onClick={async () => this.removeFixedBatchId()}
                                        >
                                            <i className="fa fa-times"></i>
                                        </button>
                                        <span className="label label-muted">{this.state.fixedBatchName} </span>
                                    </div>
                                )}
                                <div className="mt10">
                                    <SmallSelect
                                        name="objectTypeId"
                                        value={this.state.objectTypeId}
                                        handleChange={this.handleChange.bind(this)}
                                        topLabel={this.props.t('common.objectType')}
                                        placeholder={this.props.t('common.allItems', this.props.t('common.type'))}
                                        options={this.state.objectTypes}
                                    />
                                </div>

                                <div className="mt10">
                                    <SmallSelect
                                        name="inspectionStandardType"
                                        value={this.state.inspectionStandardType}
                                        handleChange={this.handleChange.bind(this)}
                                        topLabel={this.props.t('inspectionStandard.inspectionTypes')}
                                        placeholder={this.props.t('common.allItems', this.props.t('common.type'))}
                                        options={getInspectionStandardTypeOptions(this.props.t)}
                                    />
                                </div>
                                <div className="mt10">
                                    <InputSearchText
                                        name="batchSearchText"
                                        value={this.state.batchSearchText}
                                        handleChange={this.handleChange.bind(this)}
                                        handleEnter={async () => this.search()}
                                        topLabel={this.props.t('common.name')}
                                        placeholder={this.props.t('common.findItem', 'Batch')}
                                    />
                                </div>

                                <div className="mt10">
                                    <SmallSelect
                                        name="batchStatus"
                                        value={this.state.batchStatus}
                                        handleChange={this.handleChange.bind(this)}
                                        topLabel={this.props.t('common.status')}
                                        placeholder={this.props.t('common.allItems', this.props.t('common.status'))}
                                        options={[
                                            { value: BatchStates.Open, text: this.props.t('batchStatus.open') },
                                            { value: BatchStates.Active, text: this.props.t('batchStatus.active') },
                                            { value: BatchStates.Completed, text: this.props.t('batchStatus.completed') },
                                            { value: BatchStates.Proposal, text: this.props.t('batchStatus.proposal') },
                                        ]}
                                    />
                                </div>

                                <div className="mt10">
                                    <EditPanelSwitch
                                        name="showObjectsNotInSelection"
                                        value={this.state.showObjectsNotInSelection}
                                        handleChange={this.handleChange.bind(this)}
                                        placeholder={this.props.t('batch.showObjectsNotInSelection')}
                                    />
                                </div>
                            </div>
                        </form>
                        {(this.state.loading || this.state.searching) && (
                            <div className="text-center" style={{ position: 'absolute', top: '30%', left: '40%', zIndex: 100 }}>
                                <Spinner />
                            </div>
                        )}

                        <GenericMap
                            mapLoaded={this.mapLoaded.bind(this)}
                            zoom={11}
                            center={{ lat: this.state.latitude, lng: this.state.longitude }}
                            isPdokMapLayerAvailable={this.props.isPdokMapLayerAvailable}
                            isDroneNoFlyMapNlAvailable={this.props.isDroneNoFlyMapNlAvailable}
                            markers={this.state.items.map((object) => ({
                                key: object.id,
                                position: { lat: object.latitude!, lng: object.longitude! },
                                title: object.name,
                                onClick: this.handleMarkerRightClick.bind(this, object),
                                color: codeToColorCode(object.lastSurveyCondition, object.lastSurveyInspectionStandardType),
                                shapeGeoJson: object.shapeGeoJson ? JSON.parse(object.shapeGeoJson) : undefined,
                            }))}
                            onBoundsChanged={async (bounds) => {
                                await this.setState({ mapBounds: bounds });
                                await this.search.bind(this)(true);
                            }}
                            onZoomChanged={async () => {
                                await this.search.bind(this)(true);
                            }}
                            markersToFit={this.state.markersToFit}
                            shouldClusterMarkers
                        />
                    </div>
                </section>
                {!this.state.showObjectsNotInSelection && this.state.selectedObject && (
                    <PopupElement onClick={async () => await this.closePopUp()}>
                        <PanelWithoutForm>
                            <PanelBodyHeading text={this.state.selectedObject.name}>
                                <EditPanelSection>
                                    {this.state.selectedObjectBatches.map((batchObject) => (
                                        <div key={batchObject.id}>
                                            <Link to={`/batches/${batchObject.batch.id}`}>{batchObject.batch.name}</Link> (
                                            {this.props.t(`batchStatus.${batchObject.batch.status}`)})
                                            {this.state.selectedObject &&
                                                batchObject.batch.status !== BatchStates.Completed &&
                                                [Roles.Administrator, Roles.Owner].includes(this.props.userRole) && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="ml10 btn btn-default btn-xs"
                                                            onClick={async () =>
                                                                this.removeObjectFromBatch(
                                                                    this.state.selectedObject!.id,
                                                                    batchObject.batch.id,
                                                                    batchObject.id,
                                                                )
                                                            }
                                                        >
                                                            {this.props.t('common.remove')}
                                                        </button>
                                                    </>
                                                )}
                                        </div>
                                    ))}
                                </EditPanelSection>
                                <EditPanelButtons>
                                    <Link className="btn btn-default btn-sm" to={`/objects/${this.state.selectedObject.id}`}>
                                        Open object
                                    </Link>
                                    <EditPanelButton
                                        onClick={async () => this.closePopUp()}
                                        small={true}
                                        primary={true}
                                        text={this.props.t('common.cancel')}
                                    />
                                </EditPanelButtons>
                            </PanelBodyHeading>
                        </PanelWithoutForm>
                    </PopupElement>
                )}

                {this.state.showObjectsNotInSelection && this.state.selectedObject && (
                    <PopupElement onClick={async () => await this.closePopUp()}>
                        <form method="post" action="/" id="admin-form">
                            <PanelWithoutForm>
                                <PanelBodyHeading text={this.state.selectedObject.name}>
                                    <div style={{ height: '200px' }}>
                                        <EditPanelSection>
                                            <SmallFilterSelect
                                                name="batchId"
                                                value={this.state.batchId}
                                                handleChange={this.handleChange.bind(this)}
                                                topLabel="Toevoegen aan Batch"
                                                placeholder={this.props.t('common.findItem', 'Batch')}
                                                options={this.getBatchOptions()}
                                            />
                                        </EditPanelSection>
                                    </div>
                                    <EditPanelButtons>
                                        <EditPanelButton onClick={async () => this.closePopUp()} text={this.props.t('common.cancel')} small={true} />
                                        <Link className="btn btn-default btn-sm ml10" to={`/objects/${this.state.selectedObject.id}`}>
                                            Open object
                                        </Link>
                                        <EditPanelButton
                                            onClick={async () => this.addToBatch()}
                                            text={this.props.t('common.save')}
                                            small={true}
                                            primary={true}
                                        />
                                    </EditPanelButtons>
                                </PanelBodyHeading>
                            </PanelWithoutForm>
                        </form>
                    </PopupElement>
                )}
            </span>
        );
    }
    private async removeFixedBatchId() {
        await this.setState({ fixedBatchId: undefined, fixedBatchName: undefined });
        await this.search();
    }

    private async addToBatch() {
        const { batchId } = this.state;
        const objectId = this.state.selectedObject!.id;

        await fetchPost(`/api/batches/${batchId}/objects`, undefined, { batchId, objectId });

        await this.closePopUp();
    }

    private async loadFixedBatchName() {
        const response = await fetchGet(`/api/batches/${this.state.fixedBatchId}`);
        await this.setState({ fixedBatchName: response.json.name });
    }

    private async removeObjectFromBatch(objectId: string | undefined, batchId: string, batchObjectId: string) {
        if (objectId) {
            if (confirm(this.props.t('common.confirmDeleteItem'))) {
                const selectedObjectBatches = this.state.selectedObjectBatches.filter((i) => i.id !== batchObjectId);
                await this.setState({ selectedObjectBatches });
                await fetchDelete(`/api/batches/${batchId}/object/${objectId}`);
            }
        }
    }

    private async closePopUp() {
        await this.setState({ selectedObject: undefined, selectedObjectBatches: [] });
        await this.search(true);
    }

    private async loadObjectTypes() {
        const result = await fetchGet('/api/objectTypesAvailable', {}, ['NO_DATA_AVAILABLE'], [404]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            const valueTextArray = items.map((item: any) => ({ value: item.id, text: item.name }));
            await this.setState({ objectTypes: valueTextArray });
        }
    }

    private async loadBatches() {
        const queryParamters = {} as { [key: string]: string };
        if (this.state.inspectionStandardType) {
            queryParamters.inspectionStandardType = this.state.inspectionStandardType;
        }
        const result = await fetchGet('/api/batcheslist', queryParamters, [], [403]);
        const items = result.json?.items;
        if (items && items.length > 0) {
            await this.setState({ batches: items });
        } else {
            await this.setState({ batches: [] });
        }
    }

    private getBatchOptions() {
        const batchStatesToSelect = [BatchStates.Active, BatchStates.Open, BatchStates.Proposal];
        if (this.state.canSelectCompletedBatches) {
            batchStatesToSelect.push(BatchStates.Completed);
        }
        const batches = this.state.batches.filter((i) => batchStatesToSelect.includes(i.status as BatchStates));

        return batches.map((item) => ({ value: item.id, text: item.name.substring(0, 42) + (item.name.length > 43 ? '...' : '') }));
    }

    private async mapLoaded() {
        await this.setState({ loading: false });
    }

    private async handleMarkerRightClick(zxyObject: any) {
        if (this.state.showObjectsNotInSelection && ![Roles.Administrator, Roles.Owner].includes(this.props.userRole)) {
            this.props.navigate(`/objects/${zxyObject.id}`);
        } else {
            await this.setState({ selectedObject: zxyObject });

            const response = await fetchGet(`/api/objects/${zxyObject.id}/batches`);
            await this.setState({ selectedObjectBatches: response.json });
        }
    }

    private async handleChange(value: any, stateKey: string) {
        const newState = {} as any;
        newState[stateKey] = value;
        await this.setState(newState);
    }

    private async search(isKeepMapPosition = false) {
        if (
            !this.state.mapBounds ||
            (!this.state.objectTypeId &&
                !this.state.batchSearchText &&
                !this.state.inspectionStandardType &&
                !this.state.batchStatus &&
                !this.state.riskAssessment &&
                !this.state.fixedBatchId)
        ) {
            await this.setState({ items: [] });
            return;
        }

        if (this.state.isFirstSearch) {
            isKeepMapPosition = false;
            await this.setState({ isFirstSearch: false });
        }

        const url = '/api/batches/markers';

        await this.setState({ searching: true });
        const result = await fetchGet(url, {
            objectTypeId: this.state.objectTypeId,
            batchSearchText: this.state.batchSearchText ? this.state.batchSearchText : undefined,
            inspectionStandardType: this.state.inspectionStandardType,
            batchStatus: this.state.batchStatus ? this.state.batchStatus : undefined,
            risk: this.state.riskAssessment,
            showObjectsNotInSelection: this.state.showObjectsNotInSelection.toString(),
            batchId: this.state.fixedBatchId ? this.state.fixedBatchId : undefined,
            boundingBoxXMin: this.state.mapBounds.xMin,
            boundingBoxXMax: this.state.mapBounds.xMax,
            boundingBoxYMin: this.state.mapBounds.yMin,
            boundingBoxYMax: this.state.mapBounds.yMax,
        });

        if (result.json.items.length === 0) {
            await this.setState({ items: [], markersToFit: [] });
        } else {
            const { hasItemsInBoundingBox } = result.json;
            if (hasItemsInBoundingBox) {
                await this.setState({ items: result.json.items, markersToFit: [] });
            } else {
                if (!isKeepMapPosition) {
                    const markersToFit = result.json.items.map((object: IObject) => ({
                        key: object.id,
                        position: { lat: object.latitude, lng: object.longitude },
                        shapeGeoJson: object.shapeGeoJson ? JSON.parse(object.shapeGeoJson) : undefined,
                    }));
                    await this.setState({ items: result.json.items, markersToFit });
                }
            }
        }
        await this.setState({ searching: false });
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const BatchesMap = translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(BatchesMapComponent));
